import React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { add } from 'date-fns';
import { HiOutlineExclamationTriangle, HiOutlinePencilSquare } from 'react-icons/hi2';

import CalendarInputField from 'Core/components/FormElements/CalendarInputField';
import { DestructiveButton, OutlineButton, PrimaryButton } from 'Core/components/Button';
import { Modal } from 'Core/components/Layout';

const EditDeliveryDateWarningModal = ({ isOpen, onCancel, onConfirm }) => {
  const [isAcknowledged, setIsAcknowledged] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen) {
      setIsAcknowledged(false);
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} className="w-full max-w-lg">
      <div
        className="rounded-xl border border-solid border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 px-4 py-4"
        data-testid="edit-delivery-date-warning"
      >
        <div className="flex items-start gap-3.5">
          <span
            aria-hidden="true"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 ring-4 ring-white/70"
          >
            <HiOutlineExclamationTriangle className="h-6 w-6" />
          </span>
          <h3 className="!mb-0 self-center font-bold text-slate-900">Change the delivery date?</h3>
        </div>

        <p className="!mb-0 mt-3.5 text-sm leading-relaxed text-slate-700">
          The delivery date comes from the financing application and should normally not be changed
          here.
        </p>
        <p className="!mb-0 mt-2 text-sm leading-relaxed text-slate-700">
          Changing it also moves the customer&apos;s <b>first payment date</b>. Only continue if you
          need to correct a wrong date.
        </p>
      </div>

      <label className="mt-4 flex items-center text-sm text-slate-700">
        <input
          type="checkbox"
          className="mr-2 h-4 w-4"
          checked={isAcknowledged}
          onChange={(event) => setIsAcknowledged(event.target.checked)}
          data-testid="checkbox-acknowledge-edit-delivery-date"
        />
        I understand
      </label>

      <hr className="my-4 border-0 border-t border-solid border-slate-200" />

      <div className="flex justify-end gap-4">
        <DestructiveButton
          type="button"
          onClick={onConfirm}
          disabled={!isAcknowledged}
          data-testid="btn-confirm-edit-delivery-date"
        >
          Change date
        </DestructiveButton>
        <PrimaryButton type="button" onClick={onCancel} data-testid="btn-cancel-edit-delivery-date">
          Keep current date
        </PrimaryButton>
      </div>
    </Modal>
  );
};

export default function DeliveryDateField({ control, errors }) {
  const { t } = useTranslation();
  const [isEditable, setIsEditable] = React.useState(false);
  const [isWarningOpen, setIsWarningOpen] = React.useState(false);

  const confirmEdit = () => {
    setIsWarningOpen(false);
    setIsEditable(true);
  };

  return (
    <>
      <Controller
        name="deliveryDate"
        control={control}
        rules={{ required: t('common.requiredFieldDeliveryDate') }}
        render={({ field: { onChange, onBlur, name, value } }) =>
          isEditable ? (
            <CalendarInputField
              label={t('common.deliveryDate')}
              name={name}
              value={value}
              onBlur={onBlur}
              data-testid="input-delivery-date"
              onChange={onChange}
              emitRawValue
              required
              dayPickerProps={{
                disabledDays: {
                  after: add(new Date(), { days: 60 }),
                },
              }}
              errors={errors}
            />
          ) : (
            <div>
              <label htmlFor="value-delivery-date">{t('common.deliveryDate')}</label>
              <div className="mt-1 flex items-center gap-3">
                <span
                  id="value-delivery-date"
                  className="text-lg font-semibold"
                  data-testid="value-delivery-date"
                >
                  {value}
                </span>
                <OutlineButton
                  type="button"
                  onClick={() => setIsWarningOpen(true)}
                  data-testid="btn-edit-delivery-date"
                  aria-label="Edit delivery date"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <HiOutlinePencilSquare className="h-4 w-4" aria-hidden="true" />
                    Edit
                  </span>
                </OutlineButton>
              </div>
            </div>
          )
        }
      />
      <EditDeliveryDateWarningModal
        isOpen={isWarningOpen}
        onCancel={() => setIsWarningOpen(false)}
        onConfirm={confirmEdit}
      />
    </>
  );
}
